import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import type { IContaCilMesStatus } from '../../../../../hooks/useContaCilDashboard';

const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface StatItem {
  label: string;
  value: number;
  cor: string;
}

const Stat = ({ label, value, cor, delay }: StatItem & { delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Box>
      <Text
        fontSize="9.5px"
        fontWeight="600"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.05em"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text
        fontSize={{ base: '17px', md: '19px' }}
        fontWeight="700"
        color={cor}
        letterSpacing="-0.01em"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatNumber(value)}
      </Text>
    </Box>
  </motion.div>
);

interface MesResumoStripProps {
  mes: IContaCilMesStatus | null;
  meses: IContaCilMesStatus[];
  ano: number;
}

// Faixa de resumo dentro do calendário: números do mês selecionado (ou totais
// do ano). Preenche o espaço do calendário e desafoga o painel da direita.
export const MesResumoStrip = ({ mes, meses, ano }: MesResumoStripProps) => {
  const agg = mes ?? {
    total: meses.reduce((s, m) => s + m.total, 0),
    conciliados: meses.reduce((s, m) => s + m.conciliados, 0),
    pendentes: meses.reduce((s, m) => s + m.pendentes, 0),
    enviados_cliente: meses.reduce((s, m) => s + m.enviados_cliente, 0),
    empresas: Math.max(0, ...meses.map(m => m.empresas)),
  };
  const pct = agg.total > 0 ? (agg.conciliados / agg.total) * 100 : 0;
  const titulo = mes ? `Resumo de ${MESES_FULL[mes.mes - 1]}` : `Resumo do ano ${ano}`;

  const stats: StatItem[] = [
    { label: 'Lançamentos', value: agg.total, cor: '#0F172A' },
    // No ano, agg.empresas é o pico mensal (não distinct do ano) — rotula certo.
    { label: mes ? 'Empresas' : 'Pico emp./mês', value: agg.empresas, cor: '#7C3AED' },
    { label: 'Conciliados', value: agg.conciliados, cor: '#10B981' },
    { label: 'Pendentes', value: agg.pendentes, cor: '#F59E0B' },
    { label: 'Enviados cliente', value: agg.enviados_cliente, cor: '#6366F1' },
  ];

  return (
    <Box mt={5} pt={5} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
      <Flex justify="space-between" align="baseline" mb={3}>
        <Text
          fontSize="10px"
          fontWeight="600"
          color="#94A3B8"
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {titulo}
        </Text>
        <Text fontSize="11px" fontWeight="700" color={pct >= 100 ? '#10B981' : '#F59E0B'}>
          {pct.toFixed(0)}% conciliado
        </Text>
      </Flex>
      <SimpleGrid columns={{ base: 3, sm: 5 }} spacing={4} mb={3}>
        {stats.map((s, i) => (
          <Stat key={s.label} {...s} delay={0.04 * i} />
        ))}
      </SimpleGrid>
      <Box h="6px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          style={{
            height: '100%',
            borderRadius: '9999px',
            background: pct >= 100 ? '#10B981' : 'linear-gradient(to right, #F59E0B, #FBBF24)',
          }}
        />
      </Box>
    </Box>
  );
};
